import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap, tap } from 'rxjs';

import { ProjectLocationPlanCoreService } from '../../services/project-location-plan-core.service';
import { ProjectLocationPlanCoreActions } from '../state/project-location-plan-core/project-location-plan-core.actions';
import { MsgBoxType, ToastService } from '@ui';
import { TranslateService } from '@ngx-translate/core';



export const loadProjectLocationPlanCores = createEffect(
  (actions$ = inject(Actions), coreService = inject(ProjectLocationPlanCoreService)) => {
    return actions$.pipe(
      ofType(ProjectLocationPlanCoreActions.loadProjectLocationPlanCores),
      switchMap(({ projectId }) => {
        return coreService.getAll(projectId).pipe(
          map((cores) => {
            return ProjectLocationPlanCoreActions.loadProjectLocationPlanCoresSuccess(
              { cores },
            );
          }),
          catchError(() =>
            of(ProjectLocationPlanCoreActions.loadProjectLocationPlanCoresFail()),
          ),
        );
      }),
    );
  },
  { functional: true },
);

export const loadCoreTypes = createEffect(
  (actions$ = inject(Actions), coreService = inject(ProjectLocationPlanCoreService)) => {
    return actions$.pipe(
      ofType(ProjectLocationPlanCoreActions.loadCoreTypes),
      switchMap(() => {
        return coreService.findTypes().pipe(
          map((coreTypes) =>
            ProjectLocationPlanCoreActions.loadCoreTypesSuccess(
              { coreTypes },
            ),
          ),
          catchError(() =>
            of(ProjectLocationPlanCoreActions.loadCoreTypesFail()),
          ),
        );
      }),
    );
  },
  { functional: true },
);

export const updateProjectLocationPlanCores = createEffect(
  (actions$ = inject(Actions), coreService = inject(ProjectLocationPlanCoreService)) => {
    return actions$.pipe(
      ofType(ProjectLocationPlanCoreActions.updateProjectLocationPlanCores),
      switchMap(({ coreIds, changes, projectId }) => {
        const cores = coreIds.map((coreId) => {
          return {
            coreId: coreId,
            data: changes,
          }
        });

        return coreService.updateMany(projectId, { cores }).pipe(
          map((cores) =>
            ProjectLocationPlanCoreActions.updateProjectLocationPlanCoresSuccess(
              { cores },
            ),
          ),
          catchError(() =>
            of(ProjectLocationPlanCoreActions.updateProjectLocationPlanCoresFail()),
          ),
        );
      }),
    );
  },
  { functional: true },
);

export const loadPlanCores = createEffect(
  (actions$ = inject(Actions), coreService = inject(ProjectLocationPlanCoreService)) => {
    return actions$.pipe(
      ofType(ProjectLocationPlanCoreActions.loadPlanCores),
      switchMap(({ projectId, locationId, planId }) => {
        return coreService.getPlanCores(projectId, locationId, planId).pipe(
          map((cores) => {
            return ProjectLocationPlanCoreActions.loadProjectLocationPlanCoresSuccess(
              { cores },
            );
          }),
          catchError(() =>
            of(ProjectLocationPlanCoreActions.loadProjectLocationPlanCoresFail()),
          ),
        );
      }),
    );
  },
  { functional: true },
);

export const saveCore = createEffect(
  (
    actions$ = inject(Actions),
    coreService = inject(ProjectLocationPlanCoreService),
    toastService = inject(ToastService),
    translateService = inject(TranslateService),
  ) => {
    return actions$.pipe(
      ofType(ProjectLocationPlanCoreActions.saveCore),
      switchMap(({ projectId, locationId, planId, coreData }) => {
        return coreService.saveCore(projectId, locationId, planId, coreData).pipe(
          tap(() => {
            toastService.showCustomAlert(
              translateService.instant('CORE_PICKER.TOASTS.CREATE_SUCCESS') as string,
              MsgBoxType.SUCCESS,
            );
          }),
          map((core) => {
            return ProjectLocationPlanCoreActions.saveCoreSuccess(
              { core },
            );
          }),
          catchError(() =>
            of(ProjectLocationPlanCoreActions.saveCoreFail()),
          ),
        );
      }),
    );
  },
  { functional: true },
);

export const updateCore = createEffect(
  (
    actions$ = inject(Actions),
    coreService = inject(ProjectLocationPlanCoreService),
    toastService = inject(ToastService),
    translateService = inject(TranslateService),
  ) => {
    return actions$.pipe(
      ofType(ProjectLocationPlanCoreActions.updatePlanCore),
      switchMap(({ coreId, updateData, showToast }) => {
        return coreService.updateCore(coreId, updateData).pipe(
          tap(() => {
            if (showToast === false) {
              return;
            }

            toastService.showCustomAlert(
              translateService.instant('CORE_PICKER.TOASTS.UPDATE_SUCCESS') as string,
              MsgBoxType.SUCCESS,
            );
          }),
          map((core) => {
            return ProjectLocationPlanCoreActions.updatePlanCoreSuccess(
              { core },
            );
          }),
          catchError(() =>
            of(ProjectLocationPlanCoreActions.updatePlanCoreFail()),
          ),
        );
      }),
    );
  },
  { functional: true },
);

export const copyCore = createEffect(
  (
    actions$ = inject(Actions),
    coreService = inject(ProjectLocationPlanCoreService),
    toastService = inject(ToastService),
    translateService = inject(TranslateService),
  ) => {
    return actions$.pipe(
      ofType(ProjectLocationPlanCoreActions.copyCore),
      switchMap(({ coreId }) => {
        return coreService.copyCore(coreId).pipe(
          tap(() => {
            toastService.showCustomAlert(
              translateService.instant('CORE_PICKER.TOASTS.COPY_SUCCESS') as string,
              MsgBoxType.SUCCESS,
            );
          }),
          map((core) =>
            ProjectLocationPlanCoreActions.copyCoreSuccess(
              { core },
            ),
          ),
          catchError(() => {
            toastService.showCustomAlert(
              translateService.instant('CORE_PICKER.TOASTS.COPY_FAIL') as string,
              MsgBoxType.SUCCESS,
            );
            return of(ProjectLocationPlanCoreActions.copyCoreFail())
          }),
        );
      }),
    );
  },
  { functional: true },
);

export const deleteCore = createEffect(
  (
    actions$ = inject(Actions),
    coreService = inject(ProjectLocationPlanCoreService),
    toastService = inject(ToastService),
    translateService = inject(TranslateService),
  ) => {
    return actions$.pipe(
      ofType(ProjectLocationPlanCoreActions.deleteCore),
      switchMap(({ coreId }) => {
        return coreService.deleteCore(coreId).pipe(
          tap(() => {
            toastService.showCustomAlert(
              translateService.instant('CORE_PICKER.TOASTS.DELETE_SUCCESS') as string,
              MsgBoxType.SUCCESS,
            );
          }),
          map((core) =>
            ProjectLocationPlanCoreActions.deleteCoreSuccess(
              { core },
            ),
          ),
          catchError(() => {
            toastService.showCustomAlert(
              translateService.instant('CORE_PICKER.TOASTS.DELETE_FAIL') as string,
              MsgBoxType.SUCCESS,
            );
            return of(ProjectLocationPlanCoreActions.deleteCoreFail())
          }),
        );
      }),
    );
  },
  { functional: true },
);

export const bulkDeleteCores = createEffect(
  (
    actions$ = inject(Actions),
    coreService = inject(ProjectLocationPlanCoreService),
  ) => {
    return actions$.pipe(
      ofType(ProjectLocationPlanCoreActions.bulkDeleteCores),
      switchMap(({ projectId, coreIds }) => {
        return coreService.bulkDelete(projectId, coreIds).pipe(
          map(() =>
            ProjectLocationPlanCoreActions.bulkDeleteCoresSuccess({
              coreIds,
            }),
          ),
          catchError(() => {
            return of(
              ProjectLocationPlanCoreActions.bulkDeleteCoresFail(),
            );
          }),
        );
      }),
    );
  },
  { functional: true },
);

export const bulkCreateCores = createEffect(
  (
    actions$ = inject(Actions),
    coreService = inject(ProjectLocationPlanCoreService),
  ) => {
    return actions$.pipe(
      ofType(ProjectLocationPlanCoreActions.bulkCreateCores),
      switchMap(({ projectId, cores }) => {
        return coreService.bulkCreate(projectId, cores).pipe(
          map((cores) =>
            ProjectLocationPlanCoreActions.bulkCreateCoresSuccess({
              cores,
            }),
          ),
          catchError(() => {
            return of(
              ProjectLocationPlanCoreActions.bulkCreateCoresFail(),
            );
          }),
        );
      }),
    );
  },
  { functional: true },
);
